import { useMemo } from 'react'
import { SelectCategoryComboBox } from '@components/app/panels/right-panel/combo-box/select-category'
import { useTemplateCategoriesQuery } from '@/api/hooks/use-template-categories'
import {
  CURATED_TEMPLATE_CATEGORY_OPTIONS,
  type TemplateCategorySelection,
  decodeTemplateCategorySelection,
  encodeTemplateCategorySelection,
} from '@/lib/templates/template-category-selection'

const DEFAULT_SELECTION: TemplateCategorySelection = { type: 'curated', key: 'custom' }

export type SelectTemplateCategoryComboboxProps = {
  value: TemplateCategorySelection
  onChange: (selection: TemplateCategorySelection) => void
  /** Reports open/close so a host panel can track "is one of my dropdowns open" (e.g. for an
   *  Escape-key priority chain) without owning this component's internal state. */
  onOpenChange?: (open: boolean) => void
}

/**
 * Templates' Category select: "All" (synthetic — `/templates/categories` doesn't return it)
 * pinned first, then every other option — "Custom Templates" (also synthetic) and the real
 * fetched categories — in one alphabetically sorted list. Reuses the generic name-keyed
 * `SelectCategoryComboBox`, translating to/from `TemplateCategorySelection` (id-based) at the
 * boundary — the same "domain-specific wrapper around a shared generic combobox" shape
 * `SearchBackgroundKeywordsCombobox` uses for Backgrounds' own picker.
 *
 * This assumes category NAMES are unique — `SelectCategoryComboBox`'s own model is name-keyed,
 * with no id-collision handling. Acceptable for now; would need a bigger change to that shared
 * component if the real category list ever has duplicate names.
 */
export function SelectTemplateCategoryCombobox({
  value,
  onChange,
  onOpenChange,
}: SelectTemplateCategoryComboboxProps) {
  const { data: categories = [] } = useTemplateCategoriesQuery()

  const nameByValue = useMemo(() => {
    const map = new Map<string, string>()

    for (const option of CURATED_TEMPLATE_CATEGORY_OPTIONS) {
      map.set(encodeTemplateCategorySelection({ type: 'curated', key: option.key }), option.label)
    }

    for (const category of categories) {
      map.set(
        encodeTemplateCategorySelection({
          type: 'category',
          categoryId: category.templateCategoryId,
        }),
        category.categoryName
      )
    }

    return map
  }, [categories])

  const valueByName = useMemo(
    () => new Map(Array.from(nameByValue, ([encoded, name]) => [name, encoded])),
    [nameByValue]
  )

  const allLabel = CURATED_TEMPLATE_CATEGORY_OPTIONS[0].label
  const allEncodedValue = encodeTemplateCategorySelection({ type: 'curated', key: 'all' })

  // "All" is pinned first — it isn't a named category, it's the "no restriction" option. Every
  // other option (Custom Templates included) sorts alphabetically together with the real
  // categories, matching the story's example ordering.
  const options = useMemo(() => {
    const rest = Array.from(nameByValue.entries())
      .filter(([encoded]) => encoded !== allEncodedValue)
      .map(([, name]) => name)
      .sort((a, b) => a.localeCompare(b))

    return [allLabel, ...rest]
  }, [nameByValue, allLabel, allEncodedValue])
  const selectedName = nameByValue.get(encodeTemplateCategorySelection(value)) ?? allLabel

  function handleValueChange(name: string) {
    const encoded = valueByName.get(name)
    const decoded = encoded ? decodeTemplateCategorySelection(encoded) : null

    onChange(decoded ?? DEFAULT_SELECTION)
  }

  return (
    <SelectCategoryComboBox
      options={options}
      placeholder={allLabel}
      label="Category"
      value={selectedName}
      onValueChange={handleValueChange}
      onOpenChange={onOpenChange}
    />
  )
}
